'use client'
import { BookmarkIcon } from '@radix-ui/react-icons'
import { ForkIcon } from '../repositories/repository/fork-icon'
import { SidebarSection } from './components/section'
import { match } from 'ts-pattern'
import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useMemo } from 'react'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'
import {
  PublicTeam,
  isPublicTeam,
} from '@basehub/data/replicache/mutators/_public-types'
import { useReplicacheQueryV2 } from '~/hooks-data/use-replicache-query'
import { teamSpaceMutators } from '@basehub/data/replicache/mutators/team-space/mutators'
import { invariant } from '@epic-web/invariant'
import { useUserStore } from '~/app/_store/user-store'
import { Team } from '@basehub/data/replicache/index'

const icon = (key: string) =>
  match(key)
    .with('forks', () => <ForkIcon className="size-4" />)
    .with('bookmarks', () => <BookmarkIcon className="size-4" />)
    .run()

export const TeamStats = ({
  staticTeamData,
}: {
  staticTeamData?: PublicTeam | Team
}) => {
  const optimisticUser = useUserStore((s) => s.optimisticUser)
  const { team } = useSelectedTeam(staticTeamData)
  const { data: teamReplicache } = useTeamReplicache()
  const replicache = teamReplicache?.replicache

  const { data: repositories } = useReplicacheQueryV2({
    replicache,
    fetcher: (tx) => {
      invariant(teamReplicache?.teamId, "No team ID, shouldn't be enabled.")
      invariant(optimisticUser?.id, "No user ID, shouldn't be enabled.")

      return teamSpaceMutators
        .listRepositories(tx, {
          teamId: teamReplicache?.teamId,
          userId: optimisticUser?.id,
        })
        .catch(() => [])
    },
    key: ['repositories', teamReplicache?.teamId, optimisticUser?.id],
    enabled: Boolean(teamReplicache?.teamId && optimisticUser?.id),
  })

  const data = useMemo(() => {
    if (!team) return null
    const repos = isPublicTeam(team) ? team.publicRepos : repositories
    if (!repos) return null
    return repos.reduce(
      (acc, repo) => {
        acc.forks += repo.stats?.forks ?? 0
        acc.bookmarks += repo.stats?.bookmarks ?? 0
        return acc
      },
      { forks: 0, bookmarks: 0 }
    )
  }, [team, repositories])

  if (!data) return null
  return (
    <SidebarSection title="Stats">
      <ul className="flex flex-col gap-2 text-dark-bright">
        {Object.entries(data).map(([key, value]) => (
          <li className="inline-flex gap-1 items-center text-xs" key={key}>
            {icon(key)}
            <span>
              {value} {key}
            </span>
          </li>
        ))}
      </ul>
    </SidebarSection>
  )
}
